import { Copy } from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

// CopyButton is an icon button, or a labeled outline button when given text.
export function CopyButton({
	value,
	label,
	text,
	autoFocus,
	className,
}: {
	value: string;
	label: string;
	text?: string;
	autoFocus?: boolean;
	className?: string;
}) {
	const [copied, setCopied] = useState(false);
	return (
		<TooltipProvider>
			<Tooltip delayDuration={0} open={copied}>
				<TooltipTrigger asChild>
					<Button
						type="button"
						aria-label={label}
						size="sm"
						variant={text ? "outline" : "ghost"}
						autoFocus={autoFocus}
						className={cn("shrink-0", !text && "h-7", className)}
						onClick={() => {
							void navigator.clipboard.writeText(value);
							setCopied(true);
							setTimeout(() => setCopied(false), 1000);
						}}
					>
						<Copy className="h-3.5 w-3.5" />
						{text}
					</Button>
				</TooltipTrigger>
				<TooltipContent>Copied!</TooltipContent>
			</Tooltip>
		</TooltipProvider>
	);
}

// CopyableCommand copies command; display, when given, is what the page shows
// instead, such as the command with its key masked.
export function CopyableCommand({
	command,
	display,
	label,
	autoFocus,
}: {
	command: string;
	display?: string;
	label: string;
	autoFocus?: boolean;
}) {
	return (
		<div className="flex min-w-0 items-start justify-between gap-3 rounded-md border bg-muted/40 p-3">
			<pre className="min-w-0 flex-1">
				<code className="whitespace-pre-wrap break-words text-xs leading-5">{display ?? command}</code>
			</pre>
			<CopyButton value={command} label={label} autoFocus={autoFocus} />
		</div>
	);
}

// The server returns a key's value once, at creation, so this is the only
// place it can be copied from.
export function ShownOnceSecret({ value, label }: { value: string; label: string }) {
	return (
		<div className="flex max-h-32 items-start justify-between gap-2 overflow-auto rounded-md bg-muted p-2">
			<pre className="flex-1">
				<code className="whitespace-pre-wrap break-all text-xs">{value}</code>
			</pre>
			<CopyButton value={value} label={label} />
		</div>
	);
}
